/**
 * scenario.js — формат сценария урока и хелперы сборки.
 *
 * Сценарий — ЕДИНСТВЕННЫЙ ИСТОЧНИК ПРАВДЫ. Анимация, тайминг и озвучка
 * порождаются из него, а не правятся отдельно. Так 13 курсов × 8 уроков ×
 * ~14 кадров ≈ 1450 кадров остаются согласованными.
 *
 * ШАБЛОН УРОКА (модель 核桃编程 «一课九练», утв. Мином 2026-08-10):
 *   3 анимационных сегмента + блоки объяснения + задания
 * Урок = заполненный слот, а не авторское произведение.
 *
 * ТИПЫ КАДРОВ — разделение принципиально, оно из метаанализа
 * Höffler & Leutner:
 *   'act'  — ПРОЦЕДУРА: действие в интерфейсе. Анимируется. d = 1.06
 *   'idea' — КОНЦЕПЦИЯ: что это и зачем. СТАТИЧНАЯ схема. d ≈ 0.37
 *   'result' — что получилось; показ итога без новых действий
 * Не анимировать объяснения ради красоты — это прямо вредит.
 *
 * ⚠️ ПАУЗЫ ПРИНУДИТЕЛЬНЫЕ. Ученики почти не жмут паузу сами: пересматривают
 * 6.7% времени, 20% видео пропускают совсем. Поэтому паузы встроены в кадр,
 * а не отданы кнопке.
 */

import { DUR, PAUSE, estimateSpeech } from './motion.js';

/** Виды кадров. */
export const FRAME = {
    /** Процедура: действие в интерфейсе. Анимируется. */
    ACT: 'act',
    /** Концепция: объяснение. Статичная схема. */
    IDEA: 'idea',
    /** Результат: что получилось. */
    RESULT: 'result',
};

/**
 * Кадр-действие: показываем работу в интерфейсе.
 *
 * @param {string} say — реплика диктора; она же титр, она же задаёт длительность
 * @param {object} opts
 *   steps    — шаги анимации (см. StudioAnim)
 *   layout   — 'full' | 'scene'
 *   keep     — что осталось с прошлого кадра (гасит transient effect)
 */
export function act(say, opts = {}) {
    return { kind: FRAME.ACT, say, ...opts };
}

/**
 * Кадр-объяснение: СТАТИЧНАЯ схема, без движения.
 *
 * @param {string} say
 * @param {object} opts
 *   diagram — что за схема: 'axes' | 'anatomy' | 'compare' | ...
 *   labels  — подписи к элементам (spatial contiguity: подпись РЯДОМ)
 */
export function idea(say, opts = {}) {
    return { kind: FRAME.IDEA, say, ...opts };
}

/** Кадр-результат: что получилось. Лёгкое движение допустимо. */
export function result(say, opts = {}) {
    return { kind: FRAME.RESULT, say, ...opts };
}

// ── Шаги анимации ────────────────────────────────────────────────────
// Каждый шаг — маленький глагол. Длительность по умолчанию берётся из
// токенов движения, вручную её задавать обычно не нужно.

/** Курсор едет к цели. Длительность считается от расстояния — не быстрее порога. */
export const moveTo = (target, opts = {}) => ({ act: 'move', to: target, ...opts });

/** Клик. Обязательно после паузы на подсветку. */
export const click = (target, opts = {}) => ({ act: 'click', target, dur: DUR.fast, ...opts });

/** Подсветить цель ДО клика — направить внимание. */
export const point = (target, opts = {}) => ({ act: 'point', target, dur: PAUSE.beforeAction, ...opts });

/** Открыть панель. */
export const openPanel = (name, opts = {}) => ({ act: 'panel', open: name, dur: DUR.moderate, ...opts });

/** Объект появляется на сцене. */
export const spawn = (id, obj, opts = {}) => ({ act: 'spawn', id, obj, dur: DUR.appear, ...opts });

/** Объект едет по сцене. */
export const move3d = (id, delta, opts = {}) => ({ act: 'move3d', id, ...delta, dur: DUR.move3d, ...opts });

/** Выделить объект. */
export const select = (id, opts = {}) => ({ act: 'select', id, dur: DUR.moderate, ...opts });

/** Изменить свойство в панели. */
export const setProp = (name, value, opts = {}) => ({ act: 'prop', name, value, dur: DUR.moderate, ...opts });

/** Облёт камеры. */
export const orbit = (deg, opts = {}) => ({ act: 'orbit', deg, dur: DUR.orbit, ...opts });

/** Показать оси координат. */
export const axes = (show = true, opts = {}) => ({ act: 'axes', show, dur: DUR.moderate, ...opts });

/** Запустить игру. */
export const play = (on = true, opts = {}) => ({ act: 'play', on, dur: DUR.moderate, ...opts });

/** Выноска-пояснение на сцене. */
export const note = (text, opts = {}) => ({ act: 'note', text, dur: PAUSE.afterAction, ...opts });

/** ⭐ Пауза «усвоить произошедшее». Ставится ПОСЛЕ каждого действия. */
export const beat = (ms = PAUSE.afterAction) => ({ act: 'wait', dur: ms });

/**
 * ⭐ Готовая связка «показать действие правильно».
 *
 * Схема из руководств по обучению софту:
 *   подсветить цель → пауза → доехать → кликнуть → пауза на усвоение
 *
 * Использовать вместо ручного набора шагов — так темп везде одинаковый.
 */
export function doAction(target, opts = {}) {
    return [
        point(target),
        moveTo(target),
        click(target),
        beat(opts.pause ?? PAUSE.afterAction),
    ];
}

/**
 * Собрать урок из кадров.
 *
 * Длительность каждого кадра ВЫЧИСЛЯЕТСЯ (модель Duolingo): при наличии
 * озвучки — из неё, иначе оценивается по длине текста. Руками длительность
 * кадра не проставляется.
 */
export function lesson(frames, opts = {}) {
    return {
        kind: 'lesson',
        frames: frames.map((f) => ({
            ...f,
            // Оценка на случай, если озвучка недоступна. Реальная длительность
            // приходит от TTS при проигрывании.
            estMs: estimateSpeech(f.say),
        })),
        ...opts,
    };
}

/** Сумма ориентировочной длительности урока, мс. */
export function lessonDuration(l) {
    return (l.frames || []).reduce((s, f) => {
        const anim = (f.steps || []).reduce((a, st) => a + (st.dur || 300), 0);
        return s + Math.max(f.estMs || 0, anim);
    }, 0);
}

const scenario = {
    FRAME, act, idea, result, lesson, lessonDuration,
    moveTo, click, point, openPanel, spawn, move3d, select,
    setProp, orbit, axes, play, note, beat, doAction,
};
export default scenario;
